import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import logo from "@/assets/logo.png";

export default function AdminLogin() {
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // défi 2FA en attente
  const [code, setCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.requires_2fa) {
        setChallenge(data.challenge);
      } else {
        navigate("/admin");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyTwoFactor(challenge, code.trim());
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resetToPassword = () => {
    setChallenge(null);
    setCode("");
    setError("");
  };

  return (
    <div data-testid="admin-login-page" className="mx-auto flex max-w-md flex-col items-center px-4 py-20">
      <div className="w-full rounded-2xl border border-amber-800/15 bg-white p-8 shadow-lg shadow-amber-900/5">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img src={logo} alt="Pétales de Miel" data-testid="admin-login-logo" className="h-14 w-14 object-contain" />
          <h1 className="font-serif text-3xl font-semibold text-bark">Espace Miellerie</h1>
          <p className="text-sm text-bark-soft">
            {challenge ? "Saisissez le code de votre application d'authentification." : "Connectez-vous pour gérer la boutique."}
          </p>
        </div>

        {!challenge ? (
          <form data-testid="admin-login-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="admin-email" className="mb-1.5 block text-sm font-medium text-bark">Email</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep"
                placeholder="admin@abeille-bucolique.fr"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block text-sm font-medium text-bark">Mot de passe</label>
              <PasswordInput
                id="admin-password"
                data-testid="admin-password-input"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep"
                placeholder="••••••••••"
              />
            </div>

            {error && (
              <p data-testid="admin-login-error" className="rounded-xl bg-terra/10 px-4 py-3 text-sm font-medium text-terra">
                {error}
              </p>
            )}

            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-deep py-3.5 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98] disabled:opacity-60"
            >
              <Lock size={16} /> {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>
        ) : (
          <form data-testid="admin-2fa-form" onSubmit={handleVerify} className="space-y-4">
            <div className="flex items-center justify-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-deep/10 text-amber-deep">
                <ShieldCheck size={24} />
              </span>
            </div>
            <div>
              <label htmlFor="admin-2fa-code" className="mb-1.5 block text-sm font-medium text-bark">
                Code à 6 chiffres
              </label>
              <input
                id="admin-2fa-code"
                data-testid="admin-2fa-code-input"
                autoFocus
                inputMode="text"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-center text-lg tracking-[0.3em] outline-none transition-colors focus:border-amber-deep"
                placeholder="••••••"
              />
              <p className="mt-2 text-xs text-bark-soft">
                Vous pouvez aussi saisir un <strong>code de secours</strong> (format XXXX-XXXX-XXXX).
              </p>
            </div>

            {error && (
              <p data-testid="admin-2fa-error" className="rounded-xl bg-terra/10 px-4 py-3 text-sm font-medium text-terra">
                {error}
              </p>
            )}

            <button
              data-testid="admin-2fa-submit"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-deep py-3.5 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98] disabled:opacity-60"
            >
              <ShieldCheck size={16} /> {loading ? "Vérification…" : "Vérifier"}
            </button>
            <button
              type="button"
              data-testid="admin-2fa-back"
              onClick={resetToPassword}
              className="flex w-full items-center justify-center gap-1.5 text-sm font-medium text-bark-soft hover:text-amber-deep"
            >
              <ArrowLeft size={14} /> Revenir à la connexion
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
